import React, { useCallback, useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Modal,
  TextField,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import api from '../../../../../services/api';
import { useAuth } from '../../../../../hooks/auth';
import { useToast } from '../../../../../hooks/toast';
import * as S from './styles';
import {
  Emissao,
  SituacaoFiscal,
  STATUS_FINAIS_OK,
  nfceEmAndamento,
} from './useSituacaoNfce';

interface EmitirNfceModalProps {
  open: boolean;
  pedidoId: number;
  handleClose: () => void;
  /** Situação compartilhada com o cabeçalho do monitor (useSituacaoNfce). */
  situacao: SituacaoFiscal | null;
  carregando: boolean;
  recarregar: () => Promise<SituacaoFiscal | null>;
  setSituacao: React.Dispatch<React.SetStateAction<SituacaoFiscal | null>>;
}

function mensagemErro(err: any): string | undefined {
  return err?.response?.data?.message;
}

async function lerBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** Valor do select quando o terminal da retaguarda ainda não existe. */
const TERMINAL_AUTOMATICO = 'auto';

/** '' = nenhum terminal selecionado (o operador precisa escolher). */
type TerminalSelecionado = number | typeof TERMINAL_AUTOMATICO | '';

function terminalInicial(data: SituacaoFiscal): TerminalSelecionado {
  if (data.terminalPadraoId !== null) return data.terminalPadraoId;
  if (data.opcaoAutomatica) return TERMINAL_AUTOMATICO;
  return data.terminais.length === 1 ? data.terminais[0].id : '';
}

/**
 * Emissão manual de NFC-e de um pedido do delivery. Mostra a situação fiscal
 * do pedido; se ainda não há nota autorizada, permite escolher o terminal e
 * informar o CPF/CNPJ do consumidor e emitir.
 */
export function EmitirNfceModal({
  open,
  pedidoId,
  handleClose,
  situacao,
  carregando,
  recarregar,
  setSituacao,
}: EmitirNfceModalProps): JSX.Element {
  const { user } = useAuth();
  const { addToast } = useToast();
  const empresaId = user.empresa_id;

  const [emitindo, setEmitindo] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [terminalId, setTerminalId] = useState<TerminalSelecionado>('');
  const [documento, setDocumento] = useState('');
  const [nome, setNome] = useState('');

  const base = `/companies/${empresaId}/pedidos/${pedidoId}/nfce`;

  const carregar = useCallback(async () => {
    try {
      const data = await recarregar();
      if (!data) return;
      setTerminalId((atual) =>
        typeof atual === 'number' && data.terminais.some((t) => t.id === atual)
          ? atual
          : terminalInicial(data),
      );
      setDocumento((atual) => atual || data.cpfConsumidor || '');
      setNome((atual) => atual || data.nomeConsumidor || '');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao consultar a NFC-e do pedido',
        description: mensagemErro(err),
      });
    }
  }, [addToast, recarregar]);

  useEffect(() => {
    if (open) {
      setDocumento('');
      setNome('');
      setTerminalId('');
      carregar();
    }
    // Só ao abrir: as reconsultas periódicas não devem mexer no formulário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emitir = useCallback(async () => {
    setEmitindo(true);
    try {
      const { data } = await api.post<{ emissao: Emissao; mensagem: string }>(
        base,
        {
          // Sem terminal escolhido, o backend usa (ou cria) o da retaguarda.
          terminalFiscalId:
            typeof terminalId === 'number' ? terminalId : undefined,
          documento: documento.trim(),
          nome: nome.trim() || undefined,
        },
      );
      let tipo: 'success' | 'error' | 'info' = 'info';
      if (STATUS_FINAIS_OK.includes(data.emissao.status)) tipo = 'success';
      else if (data.emissao.status === 'rejected') tipo = 'error';
      addToast({
        type: tipo,
        title: data.mensagem,
      });
      setSituacao((atual) =>
        atual ? { ...atual, emissao: data.emissao } : atual,
      );
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Não foi possível emitir a NFC-e',
        description: mensagemErro(err),
      });
      carregar();
    } finally {
      setEmitindo(false);
    }
  }, [addToast, base, carregar, documento, nome, setSituacao, terminalId]);

  const baixarDanfe = useCallback(async () => {
    const chave = situacao?.emissao?.chave_acesso;
    if (!chave) return;
    setBaixando(true);
    try {
      const response = await api.get(
        `/companies/${empresaId}/nfce/${chave}/danfe?format=pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      // O navegador pode bloquear a aba aberta após o await; nesse caso baixa.
      const aba = window.open(url, '_blank');
      if (!aba) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${chave}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let description: string | undefined;
      const data = (err as any)?.response?.data;
      if (data instanceof Blob) {
        try {
          description = JSON.parse(await lerBlob(data))?.message;
        } catch {
          description = undefined;
        }
      }
      addToast({ type: 'error', title: 'Erro ao obter o DANFE', description });
    } finally {
      setBaixando(false);
    }
  }, [addToast, empresaId, situacao]);

  const emissao = situacao?.emissao ?? null;
  const autorizada = !!emissao && STATUS_FINAIS_OK.includes(emissao.status);
  const emProcessamento = emissao?.status === 'processing';
  const gerandoAutomatica = !emissao && nfceEmAndamento(situacao);
  const podeEmitir =
    !!situacao?.empresaHabilitada && !autorizada && !emProcessamento;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="emitir-nfce-titulo"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <S.Container>
        <S.Header>
          <h2 id="emitir-nfce-titulo">NFC-e do pedido #{pedidoId}</h2>
          <IconButton aria-label="Fechar" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </S.Header>

        {carregando && !situacao && (
          <S.Actions style={{ justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </S.Actions>
        )}

        {situacao && !situacao.empresaHabilitada && (
          <S.Status tone="erro">
            <strong>Empresa não habilitada para NFC-e</strong>
            Conclua a configuração fiscal (certificado, CSC e ativação) para
            emitir.
          </S.Status>
        )}

        {emissao && autorizada && (
          <S.Status tone="ok">
            <strong>
              NFC-e nº {emissao.numero ?? '-'} série {emissao.serie ?? '-'}{' '}
              {emissao.status === 'contingency'
                ? 'emitida em contingência'
                : 'autorizada'}
            </strong>
            Chave: {emissao.chave_acesso}
          </S.Status>
        )}

        {emissao && emProcessamento && (
          <S.Status tone="aguarde">
            <strong>Em processamento na SEFAZ</strong>
            Aguarde alguns segundos e atualize o status.
          </S.Status>
        )}

        {gerandoAutomatica && (
          <S.Status tone="aguarde">
            <strong>Gerando NFC-e automaticamente</strong>A nota é emitida em
            até 1 minuto depois de o pedido ser aceito. Esta tela atualiza
            sozinha; se preferir, emita agora.
          </S.Status>
        )}

        {emissao?.status === 'rejected' && (
          <S.Status tone="erro">
            <strong>Última tentativa rejeitada</strong>
            {emissao.motivo_rejeicao ?? 'Motivo não informado'}
          </S.Status>
        )}

        {emissao?.status === 'pending' && (
          <S.Status tone="aguarde">
            <strong>Nota em rascunho</strong>A transmissão não foi concluída.
            Emitir novamente reaproveita a mesma nota.
          </S.Status>
        )}

        {emissao?.status === 'cancelled' && (
          <S.Status tone="aguarde">
            <strong>A última NFC-e deste pedido foi cancelada</strong>
            Você pode emitir uma nova nota.
          </S.Status>
        )}

        {podeEmitir && situacao?.avisoTerminal && (
          <S.Status tone="aguarde">{situacao.avisoTerminal}</S.Status>
        )}

        {podeEmitir && (
          <>
            <TextField
              select
              label="Terminal fiscal"
              variant="outlined"
              size="small"
              value={terminalId}
              onChange={(e) =>
                setTerminalId(
                  e.target.value === TERMINAL_AUTOMATICO ||
                    e.target.value === ''
                    ? (e.target.value as TerminalSelecionado)
                    : Number(e.target.value),
                )
              }
              disabled={emitindo}
            >
              <MenuItem value="" disabled>
                Selecione o terminal
              </MenuItem>
              {situacao?.opcaoAutomatica && (
                <MenuItem value={TERMINAL_AUTOMATICO}>
                  {situacao.opcaoAutomatica}
                </MenuItem>
              )}
              {(situacao?.terminais ?? []).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.device_code} — série {t.serie_nfce}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="CPF/CNPJ do consumidor (opcional)"
              variant="outlined"
              size="small"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              disabled={emitindo}
              inputProps={{ inputMode: 'numeric', maxLength: 18 }}
            />
            <TextField
              label="Nome do consumidor (opcional)"
              variant="outlined"
              size="small"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={emitindo}
              inputProps={{ maxLength: 60 }}
            />
            <S.Hint>
              A taxa de entrega não entra na nota: o documento fiscal cobre os
              itens do pedido.
            </S.Hint>
          </>
        )}

        <S.Actions>
          {(emProcessamento ||
            gerandoAutomatica ||
            emissao?.status === 'pending') && (
            <Button onClick={carregar} disabled={carregando}>
              {carregando ? <CircularProgress size={18} /> : 'Atualizar status'}
            </Button>
          )}
          {autorizada && emissao?.chave_acesso && (
            <Button
              variant="contained"
              color="primary"
              onClick={baixarDanfe}
              disabled={baixando}
            >
              {baixando ? <CircularProgress size={18} /> : 'Ver DANFE'}
            </Button>
          )}
          {podeEmitir && (
            <Button
              variant="contained"
              color="primary"
              onClick={emitir}
              disabled={emitindo || terminalId === ''}
            >
              {emitindo && <CircularProgress size={18} />}
              {!emitindo &&
                (gerandoAutomatica ? 'Emitir agora' : 'Emitir NFC-e')}
            </Button>
          )}
        </S.Actions>
      </S.Container>
    </Modal>
  );
}
